import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  PageHeaderContentIndicatorColor,
  PageHeaderLocalization,
  PageHeaderSchemeDefinition,
  PageHeaderSchemeName,
  PageHeaderVariant,
  PageHeaderViolatorVerticalPosition
} from "./constants/page-header-types";
import {
  PAGE_HEADER_INDICATOR_COLOR_LIGHT,
  PAGE_HEADER_SCHEME_CONFIG,
  PAGE_HEADER_SCHEME_MAP,
  PAGE_HEADER_VARIANT_HORIZONTAL,
  PAGE_HEADER_VARIANT_IMAGE,
  PAGE_HEADER_VARIANT_OFFSET_CONTENT,
  PAGE_HEADER_VARIANT_VERTICAL
} from "./constants/page-header-constants";
import pfx from "../../utils/style-class-prefix-helper";
import {
  getFirstNestedChild,
  getNestedChildrenOf,
  getWebsiteNavigation,
  preciseSlottedChildSelector
} from "../../utils/dom-helper";
import { HeadlineTag } from "../headline/constants/headline-types";
import { clampHeadlineSizeTo } from "../../utils/headline-helper";
import { PageHeaderSchemeConfiguration } from "../../constants/colors";
import {
  IMAGE_COMPONENTS_ON_BRIGHT_IMAGE_BACKGROUND_SCHEME,
  IMAGE_COMPONENTS_ON_DARK_IMAGE_BACKGROUND_SCHEME,
  PAGE_HEADER_DEFAULT_HEADLINE_SIZE,
  PAGE_HEADER_DEFAULT_HORIZONTAL_CONTENT_ALIGNMENT,
  PAGE_HEADER_DEFAULT_VERTICAL_CONTENT_ALIGNMENT,
  PAGE_HEADER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION,
  PAGE_HEADER_DEFAULT_VIOLATOR_VERTICAL_POSITION,
  PAGE_HEADER_HEADLINE_SIZES,
  PAGE_HEADER_HORIZONTAL_CONTENT_ALIGNMENT_CENTER,
  PAGE_HEADER_HORIZONTAL_CONTENT_ALIGNMENT_LEFT,
  PAGE_HEADER_VERTICAL_CONTENT_ALIGNMENT_CENTER,
  PAGE_HEADER_VERTICAL_CONTENT_ALIGNMENT_DISTANT,
  PageHeaderHeadlineSize,
  PageHeaderHorizontalContentAlignment,
  PageHeaderVerticalContentAlignment,
  PageHeaderViolatorHorizontalPosition
} from "../../constants/global-constants";
import { scrollToElement } from "../../utils/scroll-helper";
import { BackgroundColor } from "../background/constants/background-types";
// import { InitLogging } from "../../decorators/init-logging";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { PAGE_HEADER_LOCALIZATION } from "./constants/page-header-i18n";
import Fragment from "../../common/fragment";
import { isTabletOrSmaller } from "../../utils/screen-helper";
import { StyledComponent } from "../../decorators/styled-component";

/**
 * @slot [unnamed] - Holds any number of HTML elements
 * @slot header-image - Holds a single eon-ui-image element
 * @slot header-video - Holds a single eon-ui-video-player element
 * @slot header-offset - Holds an eon-ui-image, eon-ui-clickable-image or eon-ui-video element
 * @slot header-headline - holds a single eon-ui-headline element
 * @slot header-subheadline - holds a single eon-ui-headline as subheadline element only h4, h5, h6
 * @slot header-cta - Holds a single eon-ui-button or eon-ui-link element
 */
@Component({
  tag: "eon-ui-page-header",
  styleUrl: "page-header.scss",
  shadow: true
})
export class PageHeader
  implements
    ColorableComponent<PageHeaderSchemeConfiguration<PageHeaderSchemeDefinition>>,
    LocalizableComponent<PageHeaderLocalization> {
  // @InitLogging() sentryTransaction;

  schemeConfig: PageHeaderSchemeConfiguration<PageHeaderSchemeDefinition> = PAGE_HEADER_SCHEME_CONFIG;
  schemeMap = PAGE_HEADER_SCHEME_MAP;

  localization: Localization<PageHeaderLocalization> = PAGE_HEADER_LOCALIZATION;
  currentLocalization: PageHeaderLocalization;

  private slottedHeadlines: Array<Element>;
  private slottedBulletList: Array<HTMLEonUiHeadlineElement>;
  private image: HTMLEonUiImageElement;
  private video: HTMLEonUiVideoplayerElement;
  private offsetVideo: HTMLEonUiVideoplayerElement;
  // private offsetElement: HTMLElement;
  private interactionContainerTopElement: HTMLElement;
  private interactionContainerBottomElement: HTMLElement;
  private contentContainerElement: HTMLElement;
  private resizeTimeout: NodeJS.Timeout;
  private resizeThrottleMs = 300;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: PageHeaderSchemeDefinition;
  @State() ctaLink: boolean;
  @State() subheadline: boolean;
  @State() content: boolean;
  @State() seoHeadlineTag: string;
  @State() isImageOrVideoAvailable: boolean;
  @State() heightAdjustment;
  @State() isPlaying: boolean = true;
  @State() isMuted: boolean = true;
  @State() interactionContainerRelative: boolean = false;
  @State() playToggleButtonText: string;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiPageHeaderElement;

  private headlines: HTMLEonUiHeadlineElement[];
  private hasViolator: boolean;
  private innerBackgroundMediaArea: string;

  /**
   * Raised when the component has been loaded.
   */
  @Event() pageHeaderLoad: EventEmitter;

  /**
   * Raised when the CTA in Page Header has been clicked.
   */
  @Event() pageHeaderClick: EventEmitter;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: PageHeaderSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * this property is used for headline SEO-Optimisation(not for the subheadline).<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for the name- and info-properties,<br>
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Headlize size Property
   *
   */
  @Prop() headlineSize: PageHeaderHeadlineSize = PAGE_HEADER_DEFAULT_HEADLINE_SIZE;

  /**
   * Defines the position of violator on X
   */
  // prettier-ignore
  @Prop() violatorHorizontalPosition: PageHeaderViolatorHorizontalPosition = PAGE_HEADER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION;

  /**
   * Defines the position of violator on Y
   */
  // prettier-ignore
  @Prop() violatorVerticalPosition: PageHeaderViolatorVerticalPosition = PAGE_HEADER_DEFAULT_VIOLATOR_VERTICAL_POSITION;

  /**
   * Defines various layout and design variants of Page Header
   * default variant => Example: Content and Image
   * second variant | only_image  => Content on Image variant
   * Third variant (Option not available) Content Only Variant (Do not configure imageto achieve this variant type)
   * */
  @Prop({ reflect: true, mutable: true }) variant: PageHeaderVariant = PAGE_HEADER_VARIANT_HORIZONTAL;

  /**
   * horizontal Alignment of the content-box.
   * @example left | center | right
   */
  // prettier-ignore
  @Prop({
    reflect: true,
    mutable: true
  }) horizontalContentAlignment: PageHeaderHorizontalContentAlignment = PAGE_HEADER_DEFAULT_HORIZONTAL_CONTENT_ALIGNMENT;

  /**
   * vertical Alignment of the content-box.
   * @example  top | center | bottom | distant
   */
  // prettier-ignore
  @Prop({
    mutable: true,
    reflect: true
  }) verticalContentAlignment: PageHeaderVerticalContentAlignment = PAGE_HEADER_DEFAULT_VERTICAL_CONTENT_ALIGNMENT;

  /**
   * Defines the image width and height of the page heder to be scaled across the viewport.
   * @example <eon-ui-page-header full-height=true></eon-ui-page-header>
   */
  @Prop() fullHeight: boolean;

  /**
   * Defines the height of the content overlapping elements (header, footer, ...) of the page where the page header is used. The height of the page header is calculated depending onthis height since
   * both the header and the page header together should take up to 80% of the hight of the viewport. The client should find that value dynamically
   * and pass it down.
   * @example <eon-ui-page-header content-overlapping-elements-height="148"></eon-ui-page-header>
   */
  @Prop() contentOverlappingElementsHeight: number;

  /**
   * Defines Color of full height indicator
   * @example <eon-ui-pager-header indicator-color="light"></eon-ui-pager-header>
   */
  @Prop({ reflect: true }) indicatorColor: PageHeaderContentIndicatorColor = PAGE_HEADER_INDICATOR_COLOR_LIGHT;

  componentWillLoad() {
    this.hasViolator = !!this.hostElement.querySelector('[slot="violator"]');
    this.image = preciseSlottedChildSelector(this.hostElement, "header-image", "EON-UI-IMAGE") as HTMLEonUiImageElement;
    this.video = preciseSlottedChildSelector(
      this.hostElement,
      "header-video",
      "EON-UI-VIDEOPLAYER"
    ) as HTMLEonUiVideoplayerElement;
    this.offsetVideo = preciseSlottedChildSelector(
      this.hostElement,
      "header-offset",
      "EON-UI-VIDEOPLAYER"
    ) as HTMLEonUiVideoplayerElement;
    if (this.offsetVideo) {
      this.offsetVideo.useAsBackground = true;
    }
    this.slottedHeadlines = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-headline"))?.concat(
      Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-headline-brand"))
    );
    this.slottedHeadlines.forEach((headline) => {
      headline.setAttribute("ignore-alignment", "true");
    });
    this.slottedBulletList = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-bullet-list"));
    this.setCurrentLocalization();
    this.applyScheme();
    this.imageHeightOnViewport();
    this.handleContent();
    this.handleHeadlineTag();
    this.handleAlignment();
    this.handleSoundToggleButtonText();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.handleHeightCalculation();
    this.updateInteractionContainerPositioning();
    this.pageHeaderLoad.emit();
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    let backgroundElement;
    let backgroundAppearance;

    if (this.image) backgroundElement = this.image;
    if (this.video) backgroundElement = this.video;

    if (backgroundElement) {
      switch (backgroundElement.appearance) {
        case "bright":
          backgroundAppearance = IMAGE_COMPONENTS_ON_BRIGHT_IMAGE_BACKGROUND_SCHEME;
          break;
        case "dark":
          backgroundAppearance = IMAGE_COMPONENTS_ON_DARK_IMAGE_BACKGROUND_SCHEME;
          break;
        default:
          backgroundAppearance = IMAGE_COMPONENTS_ON_DARK_IMAGE_BACKGROUND_SCHEME;
          break;
      }
    }

    if (backgroundElement) {
      // Check for variant image and offset image as these variants could come with full background media and this is then the relevant information for innerBackground.
      // For other variants the components scheme background color is still relevant for content elements. Media element should react to background appearance.
      if (this.variant === PAGE_HEADER_VARIANT_IMAGE || this.variant === PAGE_HEADER_VARIANT_OFFSET_CONTENT) {
        this.innerBackground = backgroundAppearance;
      } else {
        this.innerBackground = this.activeScheme.backgroundColor;
        this.innerBackgroundMediaArea = backgroundAppearance;
      }
    } else {
      this.innerBackground = this.activeScheme.backgroundColor;
    }
  }

  deprecatedFallback() {
    let tempVariant;
    const deprecatedVariant = this.hostElement.getAttribute("variant");
    const deprecatedLayout = this.hostElement.getAttribute("layout");

    if (deprecatedVariant === "offset-image") tempVariant = PAGE_HEADER_VARIANT_OFFSET_CONTENT;
    if (deprecatedVariant === "offset-video") tempVariant = PAGE_HEADER_VARIANT_OFFSET_CONTENT;
    if (deprecatedVariant === "default") tempVariant = PAGE_HEADER_VARIANT_HORIZONTAL;
    if (deprecatedLayout === "vertical") tempVariant = PAGE_HEADER_VARIANT_VERTICAL;
    if (deprecatedVariant === "only_image") tempVariant = PAGE_HEADER_VARIANT_IMAGE;

    if (tempVariant) this.variant = tempVariant;
  }

  @Watch("horizontalContentAlignment")
  @Watch("verticalContentAlignment")
  @Watch("variant")
  handleAlignment() {
    this.deprecatedFallback();

    /* When image is not available then always set layout to vertical
     *  As alignment condition matches for vertical layout variant and content only variant
     */
    if (!this.isImageOrVideoAvailable && this.variant !== PAGE_HEADER_VARIANT_OFFSET_CONTENT) {
      this.variant = PAGE_HEADER_VARIANT_VERTICAL;
    }

    switch (this.variant) {
      case PAGE_HEADER_VARIANT_HORIZONTAL:
        this.verticalContentAlignment = PAGE_HEADER_VERTICAL_CONTENT_ALIGNMENT_DISTANT;
        break;

      case PAGE_HEADER_VARIANT_VERTICAL:
        this.horizontalContentAlignment === PAGE_HEADER_HORIZONTAL_CONTENT_ALIGNMENT_LEFT &&
          (this.verticalContentAlignment = PAGE_HEADER_VERTICAL_CONTENT_ALIGNMENT_DISTANT);
        this.horizontalContentAlignment === PAGE_HEADER_HORIZONTAL_CONTENT_ALIGNMENT_CENTER &&
          (this.verticalContentAlignment = PAGE_HEADER_VERTICAL_CONTENT_ALIGNMENT_CENTER) &&
          this.slottedBulletList.forEach((bulletList) => {
            bulletList.setAttribute("alignable", "true");
          });
        break;

      case PAGE_HEADER_VARIANT_IMAGE:
        this.verticalContentAlignment = PAGE_HEADER_VERTICAL_CONTENT_ALIGNMENT_CENTER;
        break;

      case PAGE_HEADER_VARIANT_OFFSET_CONTENT:
        this.verticalContentAlignment = PAGE_HEADER_VERTICAL_CONTENT_ALIGNMENT_DISTANT;
        break;
    }
  }

  @Listen("buttonClick")
  @Listen("linkClick")
  handleCTAOnClick(event) {
    this.pageHeaderClick.emit(event);
  }

  @Watch("headlineHtmlTag")
  @Watch("headlineSize")
  @Watch("horizontalContentAlignment")
  handleHeadlineTag() {
    this.headlines = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-headline"));
    let headlineClampedSize = clampHeadlineSizeTo(
      PAGE_HEADER_HEADLINE_SIZES,
      this.headlineSize
    ) as PageHeaderHeadlineSize;
    this.headlines.forEach((headline) => {
      if (headline.getAttribute("slot") === "header-headline") {
        if (!!this.headlineHtmlTag) {
          headline.setAttribute("headline-html-tag", this.headlineHtmlTag);
        }
        headline.setAttribute("size", headlineClampedSize);
      } else if (headline.getAttribute("slot") === "header-subheadline") {
        headline.setAttribute("headline-html-tag", "p");
      }
    });
  }

  handleContent() {
    const button: HTMLEonUiButtonElement = getFirstNestedChild(this.hostElement, "eon-ui-button");
    const link: HTMLEonUiLinkElement = getFirstNestedChild(this.hostElement, "eon-ui-link");
    this.ctaLink = button || link ? true : false;

    const elementsInHeaderContentSlot = this.hostElement.querySelectorAll('[slot="header-content"]');
    const elementsInUnnamedSlot = this.hostElement.querySelectorAll("*:not([slot])");
    this.content = elementsInHeaderContentSlot?.length > 0 || elementsInUnnamedSlot?.length > 0;

    const subheadlineAvailable = this.hostElement.querySelectorAll('[slot="header-subheadline"]');
    this.subheadline = subheadlineAvailable && subheadlineAvailable.length > 0;
  }

  handleIndicatorClick() {
    scrollToElement(this.hostElement, {
      targetPosition: "end",
      offset:
        this.hostElement.offsetHeight +
        (Number.isInteger(this.heightAdjustment)
          ? this.heightAdjustment
          : window.innerHeight - this.hostElement.offsetHeight)
    });
  }

  /**
   * This function is calculating the page header height
   * On basis of whether Navigation or ThirdlevelNavigation is present
   * And then summation value is assigned to "content-overlapping-elements-height" property for final page header height calculation.
   */
  @Listen("resize", { target: "window" })
  @Watch("fullHeight")
  async handleHeightCalculation() {
    if (!this.contentOverlappingElementsHeight) {
      const navigationHeight = await getWebsiteNavigation()?.updateNavigationHeight();
      const thirdLevelNavigationHeight = await document
        .getElementsByTagName("eon-ui-third-level-navigation")[0]
        ?.updateHeight();
      const bottomDrawerHeight = await document.getElementsByTagName("eon-ui-bottom-drawer")[0]?.getHeight();

      this.heightAdjustment =
        (navigationHeight ? navigationHeight : 0) +
        (thirdLevelNavigationHeight ? thirdLevelNavigationHeight : 0) +
        (bottomDrawerHeight ? bottomDrawerHeight : 0);

      if (Number.isInteger(this.heightAdjustment)) {
        this.hostElement.style.setProperty("--content-overlapping-elements-height", `${this.heightAdjustment}px`);
      } else {
        this.hostElement.style.setProperty("--content-overlapping-elements-height", "0px");
      }
    }
  }

  @Watch("contentOverlappingElementsHeight")
  updateHeaderHeightCSSVariable() {
    if (Number.isInteger(this.contentOverlappingElementsHeight)) {
      this.hostElement.style.setProperty(
        "--content-overlapping-elements-height",
        `${this.contentOverlappingElementsHeight}px`
      );
    } else {
      this.hostElement.style.setProperty("--content-overlapping-elements-height", "0px");
    }
  }

  @Listen("resize", { target: "window" })
  imageHeightOnViewport() {
    this.image && this.image.setAttribute("use-as-background", "true");
    this.video && this.video.setAttribute("use-as-background", "true");

    this.isImageOrVideoAvailable = !!this.image || !!this.video;
  }

  @Listen("resize", { target: "window" })
  @Watch("isPlaying")
  handleSoundToggleButtonText() {
    if (isTabletOrSmaller()) {
      this.playToggleButtonText = "";
    } else {
      this.playToggleButtonText = this.isPlaying ? this.currentLocalization.play : this.currentLocalization.pause;
    }
  }

  handlePlayToggle() {
    if (!this.video) {
      this.isPlaying ? this.offsetVideo.pausePlayback() : this.offsetVideo.startPlayback();
    } else {
      this.isPlaying ? this.video.pausePlayback() : this.video.startPlayback();
    }
    this.isPlaying = !this.isPlaying;
  }

  handleSoundToggle() {
    this.offsetVideo.toggleSound();
    this.isMuted = !this.isMuted;
  }

  @Listen("resize", { target: "window" })
  updateInteractionContainerPositioning() {
    if (this.variant !== PAGE_HEADER_VARIANT_IMAGE) return;
    this.interactionContainerRelative = false;

    if (!this.resizeTimeout) {
      this.resizeTimeout = setTimeout(() => {
        this.resizeTimeout = null;

        if (
          this.contentContainerElement?.clientHeight +
            72 +
            this.interactionContainerTopElement?.clientHeight +
            this.interactionContainerBottomElement?.clientHeight >=
          this.hostElement.clientHeight
        ) {
          this.interactionContainerRelative = true;
        }
      }, this.resizeThrottleMs);
    }
  }

  imageContainer() {
    // currently, full height should only be available for image only variant
    const isFullHeight = this.variant === PAGE_HEADER_VARIANT_IMAGE && this.fullHeight;

    return (
      <div class={pfx("image-container")} inner-background={this.innerBackgroundMediaArea}>
        <div
          class={pfx({
            ["image-section"]: true,
            ["full-height"]: isFullHeight
          })}
        >
          {this.variant !== PAGE_HEADER_VARIANT_IMAGE && this.renderInteractionContainerTop()}
          {this.variant === PAGE_HEADER_VARIANT_IMAGE && this.contentContainer()}
          <slot name="header-image"></slot>
          <slot name="header-video"></slot>
          {this.hasViolator && this.variant !== PAGE_HEADER_VARIANT_IMAGE && (
            <div
              class={pfx({
                "page-header-violator": true,
                [`position-x-${this.violatorHorizontalPosition}`]: !!this.violatorHorizontalPosition,
                [`position-y-${this.violatorVerticalPosition}`]: !!this.violatorVerticalPosition
              })}
            >
              <slot name="violator"></slot>
            </div>
          )}
          {this.variant !== PAGE_HEADER_VARIANT_IMAGE && this.renderInteractionContainerBottom()}
        </div>
      </div>
    );
  }

  offsetContainer() {
    return (
      <div class={pfx("offset-container")}>
        {this.offsetVideo && this.renderInteractionContainerOffsetVideo()}
        <div class={pfx("offset-section")}>
          <slot name="header-offset"></slot>
        </div>
      </div>
    );
  }

  contentContainer() {
    return (
      <div
        class={pfx({
          "content-container": true,
          [`horizontal-alignment-${this.horizontalContentAlignment}`]: true,
          [`vertical-alignment-${this.verticalContentAlignment}`]: true
        })}
      >
        {(this.variant === PAGE_HEADER_VARIANT_HORIZONTAL || this.variant === PAGE_HEADER_VARIANT_VERTICAL) && (
          <div
            class={pfx({
              "content-background": true,
              gradient: this.activeScheme.gradientColor // E.ON specific. Only set if gradientColor variable of a scheme is defined.
            })}
          ></div>
        )}
        <div class={pfx("content-section")} ref={(el) => (this.contentContainerElement = el as HTMLElement)}>
          {this.hasViolator && this.variant === PAGE_HEADER_VARIANT_IMAGE && (
            <div
              class={pfx({
                "page-header-violator": true,
                [`position-x-${this.violatorHorizontalPosition}`]: !!this.violatorHorizontalPosition,
                [`position-y-${this.violatorVerticalPosition}`]: !!this.violatorVerticalPosition,
                "violator-on-image": true
              })}
            >
              <slot name="violator"></slot>
            </div>
          )}
          {this.variant === PAGE_HEADER_VARIANT_IMAGE && this.renderInteractionContainerTop()}
          <div class={pfx("headline-section")}>
            <slot name="header-headline"></slot>
          </div>
          {this.subheadline && (
            <div class={pfx("subheadline")}>
              <slot name="header-subheadline"></slot>
            </div>
          )}
          <div class={pfx("sub-content-wrapper")}>
            {this.content && (
              <div class={pfx({ "sub-content": true, ["no-subheadline"]: !this.subheadline })}>
                <slot name="header-content"></slot>
                <slot></slot>
              </div>
            )}
            {this.ctaLink && (
              <div class={pfx("cta-content")}>
                <slot name="header-cta"></slot>
              </div>
            )}
          </div>
          {this.variant === PAGE_HEADER_VARIANT_IMAGE && this.renderInteractionContainerBottom()}
        </div>
      </div>
    );
  }

  renderVariantHorizontal() {
    return (
      <Fragment>
        {this.contentContainer()}
        {this.isImageOrVideoAvailable && this.imageContainer()}
      </Fragment>
    );
  }

  renderVariantVertical() {
    return (
      <Fragment>
        {this.isImageOrVideoAvailable && this.imageContainer()}
        {this.contentContainer()}
      </Fragment>
    );
  }

  renderVariantImage() {
    return <Fragment>{this.imageContainer()}</Fragment>;
  }

  renderVariantContent() {
    return (
      <Fragment>
        <div
          class={pfx({
            "page-header-inner-wrapper": true,
            gradient: this.activeScheme.gradientColor // E.ON specific. Only set if gradientColor variable of a scheme is defined.
          })}
        >
          <slot name="header-image"></slot>
          <slot name="header-video"></slot>
          <div class={pfx("page-header-content-wrapper")}>
            {this.contentContainer()}
            {this.offsetContainer()}
          </div>
        </div>
      </Fragment>
    );
  }

  renderInteractionContainerTop() {
    return (
      <div
        class={pfx({
          "interaction-wrapper": true,
          top: true,
          relative: this.interactionContainerRelative && !!this.video
        })}
        ref={(el) => (this.interactionContainerTopElement = el as HTMLElement)}
      >
        {!!this.video && (
          <eon-ui-button
            text={this.isPlaying ? this.currentLocalization.play : this.currentLocalization.pause}
            icon={this.isPlaying ? "pause" : "play"}
            iconPosition="left"
            size="small"
            onButtonClick={() => this.handlePlayToggle()}
          ></eon-ui-button>
        )}
      </div>
    );
  }

  renderInteractionContainerBottom() {
    // currently, full height should only be available for image only variant
    const isFullHeight = this.variant === PAGE_HEADER_VARIANT_IMAGE && this.fullHeight;

    return (
      <div
        class={pfx({
          "interaction-wrapper": true,
          bottom: true,
          relative: this.interactionContainerRelative && isFullHeight
        })}
        ref={(el) => (this.interactionContainerBottomElement = el as HTMLElement)}
      >
        {isFullHeight && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <div
            class={pfx({
              ["full-height-indicator-wrapper"]: true,
              [`indicator-${this.indicatorColor}`]: this.indicatorColor
            })}
            role="button"
            tabIndex={0}
            onClick={() => this.handleIndicatorClick()}
          >
            <svg
              class={pfx({
                ["indicator-svg"]: true
              })}
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 49 48"
            >
              <path
                class={pfx({
                  ["indicator-path"]: true
                })}
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3.10826 6.55155C0.962402 9.96667 0.962402 14.6444 0.962402 24C0.962402 33.3556 0.962402 38.0333 3.10826 41.4484C4.22725 43.2293 5.7331 44.7352 7.51395 45.8541C10.9291 48 15.6068 48 24.9624 48C34.318 48 38.9957 48 42.4109 45.8541C44.1917 44.7352 45.6976 43.2293 46.8165 41.4484C48.9624 38.0333 48.9624 33.3556 48.9624 24C48.9624 14.6444 48.9624 9.96667 46.8165 6.55155C45.6976 4.7707 44.1917 3.26485 42.4109 2.14586C38.9957 0 34.318 0 24.9624 0C15.6068 0 10.9291 0 7.51395 2.14586C5.7331 3.26485 4.22725 4.7707 3.10826 6.55155ZM30.1695 25.7071C30.56 25.3166 30.56 24.6834 30.1695 24.2929C29.779 23.9024 29.1458 23.9024 28.7553 24.2929L25.9624 27.0858V18.5C25.9624 17.9477 25.5147 17.5 24.9624 17.5C24.4101 17.5 23.9624 17.9477 23.9624 18.5V27.0858L21.1695 24.2929C20.779 23.9024 20.1458 23.9024 19.7553 24.2929C19.3648 24.6834 19.3648 25.3166 19.7553 25.7071L24.2553 30.2071C24.6458 30.5976 25.279 30.5976 25.6695 30.2071L30.1695 25.7071Z"
              />
            </svg>
          </div>
        )}
      </div>
    );
  }

  renderInteractionContainerOffsetVideo() {
    return (
      <div
        class={pfx({
          "interaction-wrapper": true,
          bottom: true,
          relative: this.interactionContainerRelative && !!this.video
        })}
      >
        {!!this.video ||
          (this.offsetVideo && (
            <div class={pfx("button-wrapper")}>
              <eon-ui-button
                text={this.playToggleButtonText}
                icon={this.isPlaying ? "pause" : "play"}
                size="small"
                iconPosition="left"
                onButtonClick={() => this.handlePlayToggle()}
              ></eon-ui-button>
              <eon-ui-button
                disabled={!this.isPlaying}
                icon={this.isMuted ? "volume_mute" : "volume_normal"}
                size="small"
                onButtonClick={() => this.handleSoundToggle()}
              ></eon-ui-button>
            </div>
          ))}
      </div>
    );
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "page-header-wrapper": true,
            [`variant-${this.variant}`]: this.variant,
            "is-offset-video": this.offsetVideo,
            "has-violator": this.hasViolator,
            [`no-image`]: !this.isImageOrVideoAvailable
          })}
        >
          {this.variant === PAGE_HEADER_VARIANT_HORIZONTAL && this.renderVariantHorizontal()}
          {this.variant === PAGE_HEADER_VARIANT_VERTICAL && this.renderVariantVertical()}
          {this.variant === PAGE_HEADER_VARIANT_IMAGE && this.renderVariantImage()}
          {this.variant === PAGE_HEADER_VARIANT_OFFSET_CONTENT && this.renderVariantContent()}
        </div>
      </Host>
    );
  }
}
